import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useGameStore } from './state/gameStore';
import StatusBar from './components/HUD/StatusBar';
import RightSidebar from './components/HUD/RightSidebar';
import GameOverModal from './components/Modals/GameOverModal';
import OptimalSolutionModal from './components/Modals/OptimalSolutionModal';
import { ErrorBoundary } from './components/ErrorBoundary';
import BnBVisualizer from './components/BnB/BnBVisualizer';
import { GeometricSculptor } from '@/game/game-engine';
import { logger } from '@/app/logger';

type PerfSummary = {
  count: number
  p50: number
  p95: number
  max: number
  avg: number
}

type ResourceSnapshot = {
  activeOverlays: number
  createdGeometries: number
  disposedGeometries: number
  createdMaterials: number
  disposedMaterials: number
}

type E2EBridge = {
  requestCutPreview: (cutIndex: number) => Promise<void>
  requestConfirmCut: () => Promise<void>
  requestCancelCut: () => void
  hasActiveCutPreview: () => boolean
  requestBnBStart: () => void
  requestBnBBranch: (varIndex: number, mode?: 'one' | 'both', side?: 'le' | 'ge', nodeId?: string) => void
  requestBnBSelect: (nodeId: string) => void
  requestBnBBacktrack: (nodeId: string) => void
  requestBnBReset: () => void
  getBnBNodeIds: () => string[]
  getCurrentBnBNodeId: () => string | null
  getPerformanceSummaries: () => Record<string, PerfSummary>
  getResourceCountersSnapshot: () => ResourceSnapshot
}

type AppWindow = Window & {
  __GS_E2E__?: E2EBridge
}

const App: React.FC = () => {
  const { loading, activeModal } = useGameStore((state) => state.ui);
  const setLoading = useGameStore((state) => state.setLoading);
  const [initError, setInitError] = useState<string | null>(null);
  const engineRef = useRef<GeometricSculptor | null>(null);

  useEffect(() => {
    const handleError = (e: Event) => {
        const evt = e as CustomEvent;
        logger.error('App detected init error:', evt.detail);
        setInitError(evt.detail?.message || "Failed to initialize game engine");
    };
    window.addEventListener('game-init-error', handleError);
    return () => window.removeEventListener('game-init-error', handleError);
  }, []);

  const startEngine = useCallback(() => {
    if (engineRef.current) return;
    let engine: GeometricSculptor | null = null;
    try {
      setLoading(true);
      engine = new GeometricSculptor();
      engine.initialize();
      engineRef.current = engine;
    } catch (e: any) {
      if (engine) {
        engine.dispose();
      }
      engineRef.current = null;
      logger.error('[App] Failed to start engine', e);
      setInitError(e?.message || 'Failed to initialize game engine');
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    if (import.meta.env.VITE_E2E !== '1') return
    const appWindow = window as AppWindow

    appWindow.__GS_E2E__ = {
      requestCutPreview: async (cutIndex: number) => {
        await engineRef.current?.requestCutPreview(cutIndex)
      },
      requestConfirmCut: async () => {
        await engineRef.current?.requestConfirmCut()
      },
      requestCancelCut: () => {
        engineRef.current?.requestCancelCut()
      },
      hasActiveCutPreview: () => {
        return engineRef.current?.hasActiveCutPreview() ?? false
      },
      requestBnBStart: () => {
        engineRef.current?.requestBnBStart()
      },
      requestBnBBranch: (varIndex: number, mode: 'one' | 'both' = 'both', side?: 'le' | 'ge', nodeId?: string) => {
        engineRef.current?.requestBnBBranch(varIndex, mode, side, nodeId)
      },
      requestBnBSelect: (nodeId: string) => {
        engineRef.current?.requestBnBSelect(nodeId)
      },
      requestBnBBacktrack: (nodeId: string) => {
        engineRef.current?.requestBnBBacktrack(nodeId)
      },
      requestBnBReset: () => {
        engineRef.current?.requestBnBReset()
      },
      getBnBNodeIds: () => engineRef.current?.getBnBNodeIds() ?? [],
      getCurrentBnBNodeId: () => engineRef.current?.getCurrentBnBNodeId() ?? null,
      getPerformanceSummaries: () => engineRef.current?.getPerformanceSummaries() ?? {},
      getResourceCountersSnapshot: () =>
        engineRef.current?.getResourceCountersSnapshot() ?? {
          activeOverlays: 0,
          createdGeometries: 0,
          disposedGeometries: 0,
          createdMaterials: 0,
          disposedMaterials: 0,
        },
    }

    return () => {
      delete appWindow.__GS_E2E__
    }
  }, [])

  // Initialize game engine immediately (no auth gate)
  useEffect(() => {
    startEngine();
    return () => {
      if (engineRef.current) {
        engineRef.current.dispose();
        engineRef.current = null;
      }
    };
  }, [startEngine]);

  useEffect(() => {
    if (engineRef.current) {
      engineRef.current.invalidateRender('modal-change');
    }
  }, [activeModal]);

  return (
    <div className="relative w-screen h-screen overflow-hidden flex flex-col select-none pointer-events-none">
        {/* Top Status Bar */}
        <div className="pointer-events-auto">
             <StatusBar />
        </div>

        {/* Main Content Area (Flex Row) */}
        <div className="flex-1 flex overflow-hidden min-h-0">
            
            {/* Left/Center Spacer (Game View) */}
            <div className="flex-1 pointer-events-none relative">
                {/* Loading / Error Overlay */}
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
                        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
                    </div>
                )}
                {initError && (
                    <div className="absolute top-4 left-4 bg-red-900/90 p-4 rounded border border-red-500 max-w-md pointer-events-auto">
                        <h3 className="font-bold text-red-200">Initialization Error</h3>
                        <p className="text-xs font-mono mt-1">{initError}</p>
                    </div>
                )}
                
                {/* B&B Visualizer Overlay */}
                <div className="pointer-events-auto">
                    <ErrorBoundary componentName="BnBVisualizer">
                        <BnBVisualizer />
                    </ErrorBoundary>
                </div>
            </div>

            {/* Right Sidebar */}
            <div className="pointer-events-auto z-20 h-full flex-shrink-0">
                <ErrorBoundary componentName="RightSidebar">
                    <RightSidebar />
                </ErrorBoundary>
            </div>
        </div>

        {/* Modals */}
        <GameOverModal />
        <OptimalSolutionModal />
    </div>
  );
};

export default App;
